import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import ErrorBoundary, { RouteErrorBoundary } from './components/ErrorBoundary';

// Public Pages
import Home from './pages/Home';
import Portfolio from './pages/Portfolio';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Unsubscribe from './pages/Unsubscribe';
import Book from './pages/Book';
import ClientGallery from './pages/ClientGallery';
import PayInvoice from './pages/PayInvoice';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';

// New Public Pages (Phase 1-4)
import './pages/SubscriptionPlans.css'; // styles of lazy SubscriptionPlans — kept in the main CSS, original order (Pack C3)
import Services from './pages/Services';
import LocationPage from './pages/LocationPage';
import LocationsIndex from './pages/LocationsIndex';
import CreditPage from './pages/CreditPage';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import About from './pages/About';
import ClientLogin from './pages/ClientLogin';
import './pages/ClientPortal.css'; // styles of lazy ClientPortal — kept in the main CSS, original order (Pack C3)
import './components/AccountSecurity.css'; // styles of lazy ClientProfile — kept in the main CSS, original order (Pack C3)
import './pages/ClientProfile.css'; // styles of lazy ClientProfile — kept in the main CSS, original order (Pack C3)
import ResetPassword from './pages/ResetPassword';
import PropertyInfoForm from './pages/PropertyInfoForm';
import ReviewSubmission from './pages/ReviewSubmission';
import BookingConfirmation from './pages/BookingConfirmation';
import EmailVerification from './pages/EmailVerification';

// Components
import PackageCustomizer from './components/PackageCustomizer';

// Admin Pages
// Build timestamp: 2026-02-07 22:45 UTC - Force rebuild for complete admin pages
import './pages/admin/AdminLogin.css'; // styles of lazy AdminLogin — kept in the main CSS, original order (Pack C3)
import './pages/admin/Dashboard.css'; // styles of lazy AdminDashboard — kept in the main CSS, original order (Pack C3)
import './pages/admin/AdminResponsive.css'; // styles of lazy AdminDashboard — kept in the main CSS, original order (Pack C3)
import './pages/admin/Photographers.css'; // styles of lazy Photographers — kept in the main CSS, original order (Pack C3)
import './pages/photographer/PhotographerDashboard.css'; // styles of lazy PhotographerDashboard — kept in the main CSS, original order (Pack C3)
import './pages/photographer/Upload.css'; // styles of lazy PhotographerUpload — kept in the main CSS, original order (Pack C3)
import './pages/photographer/PhotographerProfile.css'; // styles of lazy PhotographerProfile — kept in the main CSS, original order (Pack C3)
import './pages/admin/Portfolio.css'; // styles of lazy AdminPortfolio — kept in the main CSS, original order (Pack C3)
import './pages/admin/Appointments.css'; // styles of lazy AdminAppointments — kept in the main CSS, original order (Pack C3)
import './pages/admin/Invoices.css'; // styles of lazy AdminInvoices — kept in the main CSS, original order (Pack C3)
import './pages/admin/Expenses.css'; // styles of lazy AdminExpenses — kept in the main CSS, original order (Pack C3)
import './pages/admin/ProfitLoss.css'; // styles of lazy AdminProfitLoss — kept in the main CSS, original order (Pack C3)
import PublicPropertyPage from './pages/PropertyPage';
import './pages/admin/Galleries.css'; // styles of lazy AdminGalleries — kept in the main CSS, original order (Pack C3)
import './pages/admin/Settings.css'; // styles of lazy AdminSettings — kept in the main CSS, original order (Pack C3)

// New Admin Pages (Phase 2 & 5)
import 'react-quill/dist/quill.snow.css'; // styles of lazy BlogAdmin — kept in the main CSS, original order (Pack C3)
import './pages/admin/BlogAdmin.css'; // styles of lazy BlogAdmin — kept in the main CSS, original order (Pack C3)
import './pages/admin/AnalyticsDashboard.css'; // styles of lazy AnalyticsDashboard — kept in the main CSS, original order (Pack C3)

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import RequireClientAuth from './components/RequireClientAuth';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import RecaptchaBoundary from './components/RecaptchaBoundary';

// Portals and admin load on demand (Pack C3): a homepage visitor no longer
// downloads their JavaScript. Their STYLESHEETS are still imported above, at
// each page's original position, so the CSS the site ships — and therefore how
// every page looks — is byte-identical to before (clientsplit.test.js). Some of
// those stylesheets restyle public classes like .btn-primary; moving them would
// have changed the public site. That untangling is a separate, visual decision.
import lazyWithRetry from './utils/lazyWithRetry';
const SubscriptionPlans = lazyWithRetry(() => import('./pages/SubscriptionPlans'));
const ClientPortal = lazyWithRetry(() => import('./pages/ClientPortal'));
const ClientProfile = lazyWithRetry(() => import('./pages/ClientProfile'));
const AdminLogin = lazyWithRetry(() => import('./pages/admin/Login'));
const AdminDashboard = lazyWithRetry(() => import('./pages/admin/Dashboard'));
const Photographers = lazyWithRetry(() => import('./pages/admin/Photographers'));
const PhotographerDashboard = lazyWithRetry(() => import('./pages/photographer/Dashboard'));
const PhotographerBookings = lazyWithRetry(() => import('./pages/photographer/Bookings'));
const PhotographerUpload = lazyWithRetry(() => import('./pages/photographer/Upload'));
const PhotographerAvailability = lazyWithRetry(() => import('./pages/photographer/Availability'));
const PhotographerProfile = lazyWithRetry(() => import('./pages/photographer/Profile'));
const AdminPortfolio = lazyWithRetry(() => import('./pages/admin/Portfolio'));
const AdminAppointments = lazyWithRetry(() => import('./pages/admin/Appointments'));
const AdminInvoices = lazyWithRetry(() => import('./pages/admin/Invoices'));
const AdminRevenue = lazyWithRetry(() => import('./pages/admin/Revenue'));
const AdminExpenses = lazyWithRetry(() => import('./pages/admin/Expenses'));
const AdminProfitLoss = lazyWithRetry(() => import('./pages/admin/ProfitLoss'));
const AdminLeads = lazyWithRetry(() => import('./pages/admin/Leads'));
const AdminPropertyPages = lazyWithRetry(() => import('./pages/admin/PropertyPages'));
const AdminUsers = lazyWithRetry(() => import('./pages/admin/Users'));
const AdminGalleries = lazyWithRetry(() => import('./pages/admin/Galleries'));
const AdminSettings = lazyWithRetry(() => import('./pages/admin/Settings'));
const AdminHelp = lazyWithRetry(() => import('./pages/admin/Help'));
const AdminCampaigns = lazyWithRetry(() => import('./pages/admin/Campaigns'));
const AdminPricing = lazyWithRetry(() => import('./pages/admin/Pricing'));
const AdminClients = lazyWithRetry(() => import('./pages/admin/Clients'));
const AdminCredits = lazyWithRetry(() => import('./pages/admin/AdminCredits'));
const AdminReferrals = lazyWithRetry(() => import('./pages/admin/Referrals'));
const AdminSubscriptions = lazyWithRetry(() => import('./pages/admin/Subscriptions'));
const AdminReviews = lazyWithRetry(() => import('./pages/admin/Reviews'));
const AdminContacts = lazyWithRetry(() => import('./pages/admin/Contacts'));
const BlogAdmin = lazyWithRetry(() => import('./pages/admin/BlogAdmin'));
// Admin preview of agent drafts (2026-10-16): public look, admin sign-in, noindex.
const BlogPreview = lazyWithRetry(() => import('./pages/BlogPreview'));
const AnalyticsDashboard = lazyWithRetry(() => import('./pages/admin/AnalyticsDashboard'));
const DiscountAdmin = lazyWithRetry(() => import('./pages/admin/DiscountAdmin'));

// Property pages are white-labeled microsites for the agent — they carry the
// agent's colors, their contact card and their branding. Wrapping one in Utah
// Realty Photos' own navigation undercuts that on a page the agent shares with
// their own clients. The "Photography by Utah Realty Photos" line in the page
// footer is the intended attribution.
function SiteChrome({ children }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/property/')) return null;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <div className="App">
          <a href="#main-content" className="skip-nav">Skip to main content</a>
          <SiteChrome><Navbar /></SiteChrome>
          <main id="main-content">
          {/* Route-scoped boundary: a page crash leaves the navbar and footer
              standing and clears itself on navigation. The outer boundary around
              <Router> stays as a last resort. */}
          <RouteErrorBoundary>
          {/* Shown for the moment a portal/admin file is loading (Pack C3). */}
          <Suspense fallback={<div role="status" aria-live="polite" style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: '0.95rem' }}>Loading…</div>}>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/portfolio" element={<Portfolio />} />
          {/* Public, no-auth. Every marketing email footer links here; without this
              route the link 404s and the CAN-SPAM opt-out is effectively broken. */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<RecaptchaBoundary><Contact /></RecaptchaBoundary>} />
          <Route path="/book" element={<Book />} />
          <Route path="/gallery/:uniqueLink" element={<ClientGallery />} />
          <Route path="/property/:slug" element={<PublicPropertyPage />} />
          <Route path="/pay/:invoiceNumber" element={<PayInvoice />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          {/* Stripe cancel_url for booking checkout. Was unrouted and 404d. */}
          <Route path="/payment/cancelled" element={<PaymentCancelled />} />

          {/* New Feature Routes - Phase 1-4 */}
          {/* Subscriptions retired 2026-07-24 — see HANDOFF "Subscriptions are
              hidden, not deleted". Zero ever sold; prepaid credit replaced the
              offer. Redirect rather than 404 so existing links, bookmarks and
              anything already indexed still land somewhere useful. The
              SubscriptionPlans component and every subscription route, model
              and admin screen remain intact and functional. */}
          <Route path="/credit" element={<CreditPage />} />
          {/* /pricing was linked from the homepage and blog while unrouted (404). */}
          <Route path="/pricing" element={<Pricing />} />
          {/* The cookie banner linked both of these while neither existed. */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/subscriptions" element={<Navigate to="/credit" replace />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:type" element={<Services />} />
          <Route path="/locations" element={<LocationsIndex />} />
          <Route path="/locations/:city" element={<LocationPage />} />
          <Route path="/customize" element={<RequireClientAuth><PackageCustomizer /></RequireClientAuth>} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* 2026-10-16: author page for blog bylines, and the admin draft preview. */}
          <Route path="/about" element={<About />} />
          <Route path="/blog-preview/:id" element={<BlogPreview />} />
          <Route path="/client/login" element={<RecaptchaBoundary><ClientLogin /></RecaptchaBoundary>} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/client/portal" element={<ClientPortal />} />
          <Route path="/client/profile" element={<RequireClientAuth><ClientProfile /></RequireClientAuth>} />
          <Route path="/verify-email/:token" element={<EmailVerification />} />
          <Route path="/property-info/:appointmentId" element={<PropertyInfoForm />} />
          <Route path="/review/:bookingId" element={<ReviewSubmission />} />
          <Route path="/review" element={<ReviewSubmission />} />
          <Route path="/booking/confirmed/:bookingId" element={<BookingConfirmation />} />
          <Route path="/booking/confirmed" element={<BookingConfirmation />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<PrivateRoute allowedRoles={['admin']}><AdminDashboard /></PrivateRoute>} />
          <Route path="/admin/photographers" element={<PrivateRoute allowedRoles={['admin']}><Photographers /></PrivateRoute>} />
          <Route path="/admin/portfolio" element={<PrivateRoute allowedRoles={['admin']}><AdminPortfolio /></PrivateRoute>} />
          <Route path="/admin/appointments" element={<PrivateRoute allowedRoles={['admin']}><AdminAppointments /></PrivateRoute>} />
          <Route path="/admin/invoices" element={<PrivateRoute allowedRoles={['admin']}><AdminInvoices /></PrivateRoute>} />
          <Route path="/admin/credits" element={<PrivateRoute allowedRoles={['admin']}><AdminCredits /></PrivateRoute>} />
          <Route path="/admin/referrals" element={<PrivateRoute allowedRoles={['admin']}><AdminReferrals /></PrivateRoute>} />
          <Route path="/admin/revenue" element={<PrivateRoute allowedRoles={['admin']}><AdminRevenue /></PrivateRoute>} />
          <Route path="/admin/expenses" element={<PrivateRoute allowedRoles={['admin']}><AdminExpenses /></PrivateRoute>} />
          <Route path="/admin/profit-loss" element={<PrivateRoute allowedRoles={['admin']}><AdminProfitLoss /></PrivateRoute>} />
          <Route path="/admin/leads" element={<PrivateRoute allowedRoles={['admin']}><AdminLeads /></PrivateRoute>} />
          <Route path="/admin/property-pages" element={<PrivateRoute allowedRoles={['admin']}><AdminPropertyPages /></PrivateRoute>} />
          <Route path="/admin/users" element={<PrivateRoute allowedRoles={['admin']}><AdminUsers /></PrivateRoute>} />
          <Route path="/admin/galleries" element={<PrivateRoute allowedRoles={['admin']}><AdminGalleries /></PrivateRoute>} />
          <Route path="/admin/campaigns" element={<PrivateRoute allowedRoles={['admin']}><AdminCampaigns /></PrivateRoute>} />
          <Route path="/admin/help" element={<PrivateRoute allowedRoles={['admin']}><AdminHelp /></PrivateRoute>} />
          <Route path="/admin/settings" element={<PrivateRoute allowedRoles={['admin']}><AdminSettings /></PrivateRoute>} />
          <Route path="/admin/pricing" element={<PrivateRoute allowedRoles={['admin']}><AdminPricing /></PrivateRoute>} />
          
          {/* Photographer Portal Routes */}
          <Route path="/photographer/login" element={<AdminLogin />} />
          <Route path="/photographer" element={<PrivateRoute allowedRoles={['photographer', 'admin']}><PhotographerDashboard /></PrivateRoute>} />
          <Route path="/photographer/bookings" element={<PrivateRoute allowedRoles={['photographer', 'admin']}><PhotographerBookings /></PrivateRoute>} />
          <Route path="/photographer/upload" element={<PrivateRoute allowedRoles={['photographer', 'admin']}><PhotographerUpload /></PrivateRoute>} />
          <Route path="/photographer/availability" element={<PrivateRoute allowedRoles={['photographer', 'admin']}><PhotographerAvailability /></PrivateRoute>} />
          <Route path="/photographer/profile" element={<PrivateRoute allowedRoles={['photographer', 'admin']}><PhotographerProfile /></PrivateRoute>} />
          
          {/* New Admin Routes - Phase 2 & 5 */}
          <Route path="/admin/blog" element={<PrivateRoute allowedRoles={['admin']}><BlogAdmin /></PrivateRoute>} />
          <Route path="/admin/blog/:id" element={<PrivateRoute allowedRoles={['admin']}><BlogAdmin /></PrivateRoute>} />
          <Route path="/admin/analytics" element={<PrivateRoute allowedRoles={['admin']}><AnalyticsDashboard /></PrivateRoute>} />
          <Route path="/admin/discounts" element={<PrivateRoute allowedRoles={['admin']}><DiscountAdmin /></PrivateRoute>} />
          <Route path="/admin/clients" element={<PrivateRoute allowedRoles={['admin']}><AdminClients /></PrivateRoute>} />
          <Route path="/admin/subscriptions" element={<PrivateRoute allowedRoles={['admin']}><AdminSubscriptions /></PrivateRoute>} />
          <Route path="/admin/reviews" element={<PrivateRoute allowedRoles={['admin']}><AdminReviews /></PrivateRoute>} />
          <Route path="/admin/contacts" element={<PrivateRoute allowedRoles={['admin']}><AdminContacts /></PrivateRoute>} />

          {/* 404 Catch-all */}
          <Route path="*" element={
            <div style={{ textAlign: 'center', paddingTop: '200px', minHeight: '60vh' }}>
              <h1 style={{ fontSize: '3rem', color: '#1f2937', marginBottom: '1rem' }}>404</h1>
              <p style={{ fontSize: '1.2rem', color: '#6b7280', marginBottom: '2rem' }}>Page not found</p>
              <a href="/" style={{ padding: '12px 24px', background: '#2563eb', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600' }}>Go Home</a>
            </div>
          } />
        </Routes>
          </Suspense>
          </RouteErrorBoundary>
          </main>
        <SiteChrome><Footer /></SiteChrome>
        <CookieConsent />
        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
